
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CooksyLogo } from "@/components/CooksyLogo";
import { Card } from "@/components/ui/card";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Storage keys for redirect flags
const REDIRECT_HANDLED_KEY = 'auth_redirect_handled';
const IS_NEW_USER_KEY = 'is_new_user';

const AuthPage: React.FC = () => {
  const [isSignIn, setIsSignIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const params = new URLSearchParams(window.location.search);
      const confirmToken = params.get('confirmation_token');
      
      if (confirmToken) {
        setLoading(true);
        try {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: confirmToken,
            type: 'signup'
          });

          if (error) {
            console.error("Email verification error:", error);
            setError("Email verification failed. Please try again.");
          } else {
            console.log("Email verified successfully");
            // Mark as new user after email verification
            sessionStorage.setItem(IS_NEW_USER_KEY, 'true');
            sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'false');
            navigate('/onboarding', { replace: true });
          }
        } catch (err) {
          console.error("Verification error:", err);
          setError("An unexpected error occurred. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    };

    handleEmailConfirmation();
  }, [location.search, navigate]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session check error:", sessionError);
          return;
        }

        if (session) {
          console.log("Session found, checking profile status");
          
          // If we already handled redirect, don't do it again
          const redirectHandled = sessionStorage.getItem(REDIRECT_HANDLED_KEY) === 'true';
          if (redirectHandled) {
            console.log("Redirect already handled, skipping");
            return;
          }
          
          // Force check for new user status from session storage first
          const isNewUser = sessionStorage.getItem(IS_NEW_USER_KEY) === 'true';
          console.log("Is new user from storage:", isNewUser);
          
          if (isNewUser) {
            console.log("New user detected, redirecting to onboarding");
            sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'true');
            navigate('/onboarding', { replace: true });
            return;
          }
          
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('id', session.user.id)
            .single();

          if (profileError) {
            console.error("Profile fetch error:", profileError);
            return;
          }

          if (profile?.onboarding_completed === true) {
            console.log("Onboarding completed, redirecting to home");
            sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'true');
            navigate('/', { replace: true });
          } else {
            console.log("Onboarding incomplete, redirecting to onboarding");
            sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'true');
            navigate('/onboarding', { replace: true });
          }
        }
      } catch (err) {
        console.error("Session check error:", err);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state change on Auth page:", event, session?.user?.id);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (session) {
          // If we already handled redirect, don't do it again
          const redirectHandled = sessionStorage.getItem(REDIRECT_HANDLED_KEY) === 'true';
          if (redirectHandled) {
            console.log("Redirect already handled, skipping");
            return;
          }
          
          console.log("User authenticated, checking new user status");
          
          // Check if this is a new user - check session storage first
          const isNewUser = sessionStorage.getItem(IS_NEW_USER_KEY) === 'true';
          console.log("Is new user from storage:", isNewUser);
          
          // Mark that we've handled the redirect
          sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'true');

          if (isNewUser) {
            console.log("New user detected, redirecting to onboarding");
            navigate('/onboarding', { replace: true });
            return;
          }
          
          // If not marked as new user, check profile status
          const { data: profile } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('id', session.user.id)
            .single();

          if (profile?.onboarding_completed === true) {
            console.log("Profile complete, redirecting to home");
            navigate('/', { replace: true });
          } else {
            console.log("Profile incomplete, redirecting to onboarding");
            navigate('/onboarding', { replace: true });
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setLoading(false);
    setEmailSent(true);
    setError(null);
  };

  const validateInputs = () => {
    if (!email || !password) {
      setError("Please fill in all fields");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    if (!isSignIn && password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateInputs()) return;
    if (loading) return;
    
    setLoading(true);
    console.log("Starting auth process:", isSignIn ? "sign in" : "sign up");

    try {
      // Clear any stale flags
      sessionStorage.removeItem(REDIRECT_HANDLED_KEY);
      
      if (isSignIn) {
        // Mark as existing user
        console.log("Signing in, marking as existing user");
        sessionStorage.setItem(IS_NEW_USER_KEY, 'false');
        
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          console.error("Sign in error:", error);
          if (error.message === "Invalid login credentials") {
            setError("Invalid email or password");
          } else {
            setError(error.message);
          }
        } else {
          // Let onAuthStateChange handle the redirect
          sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'false');
        }
      } else {
        // Mark as new user BEFORE the async signup operation
        console.log("Signing up, marking as new user");
        sessionStorage.setItem(IS_NEW_USER_KEY, 'true');
        
        // Ensure we're passing the first and last names to user metadata when signing up
        const { data, error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth?confirmation=true`,
            data: {
              first_name: "",  // Currently not collecting in Auth page
              last_name: "",   // Currently not collecting in Auth page
              email_confirm_redirect: `${window.location.origin}/auth?confirmation=true`
            }
          }
        });
        
        if (error) {
          console.error("Sign up error:", error);
          setError(error.message);
          return;
        }

        // Double-check the new user flag is set
        console.log("After signup, checking IS_NEW_USER_KEY:", sessionStorage.getItem(IS_NEW_USER_KEY));

        // If we have an immediate session, we're already logged in
        if (data.session) {
          console.log("User signed up and logged in immediately, redirecting to onboarding");
          sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'false');
          navigate('/onboarding', { replace: true });
          return;
        } else if (data.user) {
          // Email confirmation required
          resetForm();
          toast({
            title: "Verification email sent",
            description: "Please check your email to complete your registration.",
          });
          return;
        }
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        toast({
          title: "Check your email",
          description: "We've sent you a password reset link.",
        });
        setShowResetDialog(false);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex flex-col justify-center items-center px-4 py-12">
          <div className="w-full max-w-md space-y-8">
            <div className="flex flex-col items-center space-y-6">
              <CooksyLogo showText={false} />
              <div className="text-center space-y-2">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                  Check Your Email
                </h1>
                <p className="text-muted-foreground">
                  We've sent you a verification link. Please check your email to complete registration.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-12">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center space-y-6">
            <CooksyLogo showText={false} />
            <div className="text-center space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                Welcome to HomePlate
              </h1>
              <p className="text-muted-foreground">
                {isSignIn 
                  ? "Sign in to your account to continue"
                  : "Create your account to get started"}
              </p>
            </div>
          </div>

          <Card className="p-6">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex mb-6">
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  isSignIn
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => {
                  setIsSignIn(true);
                  setError(null);
                }}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  !isSignIn
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => {
                  setIsSignIn(false);
                  setError(null);
                }}
              >
                Sign Up
              </button>
            </div>

            <form onSubmit={handleAuth} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {isSignIn ? "Password" : "Create a Password"}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {!isSignIn && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {!isSignIn && (
                <p className="text-xs text-muted-foreground mt-2">
                  By clicking 'Sign up', you agree to the <Link to="/terms" className="text-primary hover:underline">Terms and Conditions</Link>.
                </p>
              )}

              <Button 
                type="submit" 
                className="w-full bg-[#FF6B2C] hover:bg-[#FF6B2C]/90" 
                disabled={loading}
              >
                {loading ? "Please wait..." : (isSignIn ? "Sign in" : "Sign up")}
              </Button>

              {isSignIn && (
                <button
                  type="button"
                  onClick={() => setShowResetDialog(true)}
                  className="w-full text-sm text-primary hover:text-primary/90"
                >
                  Forgot password?
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setIsSignIn(!isSignIn);
                  setError(null);
                }}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                {isSignIn 
                  ? "Don't have an account? Sign up" 
                  : "Already have an account? Sign in"}
              </button>
            </form>
          </Card>
        </div>
      </div>

      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="Enter your email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#FF6B2C] hover:bg-[#FF6B2C]/90"
              disabled={loading}
            >
              {loading ? "Please wait..." : "Send Reset Link"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;
